import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { groupApi } from '../../../core/api/rtk-query/group.api';
import { eventApi } from '../../../core/api/rtk-query/event.api';
import { userApi } from '../../../core/api/rtk-query/user.api';
import toast from 'react-hot-toast';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(13, 25, 41, 0.36);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  width: 800px;
  max-width: calc(100% - 24px);
  max-height: calc(100vh - 48px);
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e8f0;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.08);
  padding: 24px;
  display: grid;
  gap: 16px;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #0b1221;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Select = styled.select`
  flex: 1;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #dbe3f6;
  background: #fff;
  color: #0b1221;
  font-size: 14px;
`;

const Input = styled.input`
  flex: 1;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #dbe3f6;
  background: #fff;
  color: #0b1221;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #0b68fe;
  }
`;

const ComboWrap = styled.div`
  position: relative;
  flex: 1;
`;

const ComboList = styled.ul`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  z-index: 50;
  list-style: none;
  margin: 0;
  padding: 8px 0;
  max-height: 240px;
  overflow: auto;
  background: #fff;
  border: 1px solid #dbe3f6;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
`;

const ComboItem = styled.li`
  padding: 8px 12px;
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 14px;
  &:hover {
    background: #f7f9ff;
  }
`;

const Btn = styled.button`
  background: #fff;
  border: 1px solid #dbe3f6;
  color: #0b1221;
  padding: 8px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  &:hover:not(:disabled) {
    background: #f7f9ff;
    border-color: #0b68fe;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryBtn = styled(Btn)`
  background: linear-gradient(180deg, #0b68fe 0%, #0a5be3 100%);
  color: #fff;
  border: none;
  &:hover:not(:disabled) {
    background: linear-gradient(180deg, #0a5be3 0%, #0950c9 100%);
  }
`;

const SelectedUsers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: #f7f9ff;
  border-radius: 10px;
  min-height: 48px;
`;

const UserChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #dbe3f6;
  border-radius: 8px;
  font-size: 13px;
  color: #0b1221;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: #d32f2f;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
  line-height: 1;
  &:hover {
    color: #b71c1c;
  }
`;

const Section = styled.div`
  display: grid;
  gap: 12px;
  padding: 16px;
  background: #fafbfc;
  border-radius: 12px;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #0b1221;
`;

interface GrantAccessModalProps {
  eventId: string;
  onClose: () => void;
}

export const GrantAccessModal = ({ eventId, onClose }: GrantAccessModalProps) => {
  const [groupId, setGroupId] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [userOpen, setUserOpen] = useState(false);
  const [triggerUserSearch, userSearch] = userApi.useLazySearchUsersQuery();
  const [triggerGetGroupUsers] = groupApi.useLazyGetUsersInGroupQuery();
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const { data: active } = groupApi.useGetActiveAdminGroupsQuery({ pageNumber: 1, pageSize: 100 });
  const [grantForGroup, { isLoading: loadingGroup }] = eventApi.useGrantEventAccessForGroupMutation();
  const [addToUsers, { isLoading: loadingUsers }] = eventApi.useAddEventToUsersMutation();

  const handleAddGroup = async () => {
    if (!groupId) return;
    try {
      await grantForGroup({ eventId, groupId }).unwrap();
      toast.success('Доступ выдан курсу!');
      setGroupId('');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Ошибка при выдаче доступа');
    }
  };

  const handleSelectAllFromGroup = async () => {
    if (!groupId) {
      toast.error('Выберите курс');
      return;
    }
    try {
      // Получаем всех пользователей из группы
      const result = await triggerGetGroupUsers({ groupId, pageNumber: 1, pageSize: 1000 }).unwrap();
      
      if (result?.users && result.users.length > 0) {
        const users = result.users;
        const newUserIds = users.map((u: any) => u.id).filter((id: string) => !selectedUserIds.includes(id));
        const newUsers = users.filter((u: any) => !selectedUserIds.includes(u.id));
        
        setSelectedUserIds([...selectedUserIds, ...newUserIds]);
        setSelectedUsers([...selectedUsers, ...newUsers]);
        toast.success(`Добавлено ${newUsers.length} студентов`);
      } else {
        toast('В курсе нет студентов');
      }
    } catch (error) {
      console.error('Error loading group users:', error);
      toast.error('Ошибка при загрузке студентов курса');
    }
  };

  const handleAddSelectedUsers = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('Выберите хотя бы одного пользователя');
      return;
    }
    try {
      await addToUsers({ id: eventId, userIds: selectedUserIds }).unwrap();
      toast.success(`Доступ выдан ${selectedUserIds.length} пользователям!`);
      setSelectedUserIds([]);
      setSelectedUsers([]);
    } catch (error: any) {
      toast.error(error?.data?.message || 'Ошибка при выдаче доступа');
    }
  };

  const handleSelectUser = (user: any) => {
    if (!selectedUserIds.includes(user.id)) {
      setSelectedUserIds([...selectedUserIds, user.id]);
      setSelectedUsers([...selectedUsers, user]);
    }
    setUserQuery('');
    setUserOpen(false);
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  useEffect(() => {
    const timeout = setTimeout(async () => {
      const q = userQuery.trim();
      if (!q) {
        setUserOptions([]);
        return;
      }
      const isNum = /^\d+$/.test(q);
      const tasks: Promise<any>[] = [];
      tasks.push(triggerUserSearch({ name: q, pageNumber: 1, pageSize: 25 }).unwrap());
      tasks.push(triggerUserSearch({ email: q, pageNumber: 1, pageSize: 25 }).unwrap());
      tasks.push(triggerUserSearch({ isuGroup: q, pageNumber: 1, pageSize: 25 }).unwrap());
      if (isNum) tasks.push(triggerUserSearch({ isuNumber: Number(q), pageNumber: 1, pageSize: 25 }).unwrap());
      const settled = await Promise.allSettled(tasks);
      const merged: Record<string, any> = {};
      settled.forEach(r => {
        if (r.status === 'fulfilled') {
          for (const u of r.value?.users || []) merged[u.id] = u;
        }
      });
      setUserOptions(Object.values(merged));
    }, 300);
    return () => clearTimeout(timeout);
  }, [userQuery, triggerUserSearch]);

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>Выдать доступ к мероприятию</Title>
          <Btn onClick={onClose}>✕</Btn>
        </Header>

        <Section>
          <SectionTitle>Выдать доступ курсу</SectionTitle>
          <Row>
            <Select value={groupId} onChange={e => setGroupId(e.target.value)}>
              <option value="">Выберите курс...</option>
              {active?.groups.map(g => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
            <PrimaryBtn onClick={handleAddGroup} disabled={!groupId || loadingGroup}>
              {loadingGroup ? 'Добавление...' : 'Выдать доступ'}
            </PrimaryBtn>
          </Row>
          <Btn onClick={handleSelectAllFromGroup}>
            + Выбрать всех из курса
          </Btn>
        </Section>

        <Section>
          <SectionTitle>Выдать доступ отдельным пользователям</SectionTitle>
          <Row>
            <ComboWrap>
              <Input
                placeholder="Поиск по имени, email или ISU..."
                value={userQuery}
                onChange={e => {
                  setUserQuery(e.target.value);
                  setUserOpen(true);
                }}
                onFocus={() => setUserOpen(true)}
                onBlur={() => setTimeout(() => setUserOpen(false), 120)}
              />
              {userOpen && userOptions.length > 0 && (
                <ComboList>
                  {userOptions.map((u: any) => (
                    <ComboItem
                      key={u.id}
                      onMouseDown={() => handleSelectUser(u)}
                      style={{
                        opacity: selectedUserIds.includes(u.id) ? 0.5 : 1,
                        pointerEvents: selectedUserIds.includes(u.id) ? 'none' : 'auto',
                      }}
                    >
                      {u.name} ({u.isuGroup || '—'}) • ISU {u.isuNumber}
                    </ComboItem>
                  ))}
                </ComboList>
              )}
            </ComboWrap>
          </Row>

          {selectedUsers.length > 0 && (
            <>
              <div style={{ fontSize: 13, color: '#667085' }}>Выбрано пользователей: {selectedUsers.length}</div>
              <SelectedUsers>
                {selectedUsers.map(u => (
                  <UserChip key={u.id}>
                    {u.name} (ISU {u.isuNumber})
                    <RemoveBtn onClick={() => handleRemoveUser(u.id)}>×</RemoveBtn>
                  </UserChip>
                ))}
              </SelectedUsers>
              <PrimaryBtn onClick={handleAddSelectedUsers} disabled={loadingUsers}>
                {loadingUsers ? 'Добавление...' : `Выдать доступ (${selectedUsers.length})`}
              </PrimaryBtn>
            </>
          )}
        </Section>

        <Row style={{ justifyContent: 'flex-end', marginTop: '8px' }}>
          <Btn onClick={onClose}>Закрыть</Btn>
        </Row>
      </Modal>
    </Backdrop>
  );
};
